/**
 * Context tab — Datasets section (FR-2.2, DC-1792 demo D2).
 *
 * The `data` channel's input: which dataset(s) evidence this use case.
 * Honesty rules carried into the UI: an unbound use case STATES it — the
 * copy names the consequence (data-leg tests will report "no dataset
 * bound", never 0) so absence reads as a decision to make, not a blank.
 * Viewers read; analysts and admins bind from the org's datasets and
 * unbind. Cross-org datasets never appear — the API is RLS-scoped and the
 * schema makes a foreign binding unrepresentable.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { usePermissions } from '../../hooks/useAuth';

interface DatasetBinding {
  id: string;
  journeyId: string;
  journeyName: string;
  createdAt: string;
}

interface BindableDataset {
  journeyId: string;
  name: string;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'ready'; datasets: DatasetBinding[] };

export const UseCaseDatasets: React.FC<{ useCaseId: string }> = ({ useCaseId }) => {
  const { get, post, delete: del } = useApiClient();
  const { isAdmin, isAnalyst } = usePermissions();
  const canEdit = isAdmin || isAnalyst;

  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [available, setAvailable] = useState<BindableDataset[]>([]);
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await get<{ data?: { datasets?: DatasetBinding[] } }>(
        `/api/use-cases/${useCaseId}/datasets`
      );
      setState({ phase: 'ready', datasets: res.data?.datasets ?? [] });
      if (canEdit) {
        const avail = await get<{ data?: { datasets?: BindableDataset[] } }>(
          `/api/use-cases/${useCaseId}/datasets/available`
        );
        setAvailable(avail.data?.datasets ?? []);
      }
    } catch {
      setState({ phase: 'error' });
    }
  }, [get, useCaseId, canEdit]);

  useEffect(() => {
    void load();
  }, [load]);

  const bind = useCallback(async () => {
    if (!chosen) return;
    setBusy(true);
    setActionError(null);
    try {
      await post(`/api/use-cases/${useCaseId}/datasets`, { journeyId: chosen });
      setChosen('');
      await load();
    } catch {
      setActionError('The dataset could not be bound.');
    } finally {
      setBusy(false);
    }
  }, [post, useCaseId, chosen, load]);

  const unbind = useCallback(
    async (bindingId: string) => {
      setBusy(true);
      setActionError(null);
      try {
        await del(`/api/use-cases/${useCaseId}/datasets/${bindingId}`);
        await load();
      } catch {
        setActionError('The dataset could not be unbound.');
      } finally {
        setBusy(false);
      }
    },
    [del, useCaseId, load]
  );

  const bound = state.phase === 'ready' ? state.datasets : [];
  const boundIds = new Set(bound.map((b) => b.journeyId));
  const bindable = available.filter((a) => !boundIds.has(a.journeyId));

  return (
    <div className="uc-datasets" data-testid="uc-datasets">
      {state.phase === 'loading' && (
        <p className="uc-datasets-note" aria-busy="true">
          Reading dataset bindings…
        </p>
      )}
      {state.phase === 'error' && (
        <p className="uc-datasets-note" role="alert" data-testid="uc-datasets-error">
          The dataset bindings could not be read.
        </p>
      )}
      {state.phase === 'ready' && bound.length === 0 && (
        <p className="uc-datasets-note" data-testid="uc-datasets-empty">
          No dataset is bound to this use case. Until one is, every data-leg
          test will honestly report “no dataset bound” — never a zero.
        </p>
      )}
      {state.phase === 'ready' && bound.length > 0 && (
        <ul className="uc-datasets-list" data-testid="uc-datasets-list">
          {bound.map((b) => (
            <li key={b.id} className="uc-datasets-row">
              <span className="uc-datasets-name">{b.journeyName}</span>
              {canEdit && (
                <button
                  type="button"
                  className="banking-btn banking-btn-secondary"
                  data-testid={`uc-datasets-unbind-${b.id}`}
                  disabled={busy}
                  onClick={() => void unbind(b.id)}
                >
                  Unbind
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {state.phase === 'ready' && canEdit && (
        <div className="uc-datasets-bind-row">
          <select
            aria-label="Dataset to bind"
            data-testid="uc-datasets-picker"
            value={chosen}
            disabled={busy}
            onChange={(e) => setChosen(e.target.value)}
          >
            <option value="">Choose a dataset…</option>
            {bindable.map((a) => (
              <option key={a.journeyId} value={a.journeyId}>
                {a.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="banking-btn banking-btn-primary"
            data-testid="uc-datasets-bind"
            disabled={busy || chosen === ''}
            onClick={() => void bind()}
          >
            Bind dataset
          </button>
        </div>
      )}
      {actionError && (
        <p className="uc-datasets-note" role="alert">
          {actionError}
        </p>
      )}
    </div>
  );
};
